import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import useCreateTrip from "../../components/CommonComponents/CreateTrip/useCreateTrip";
import { ERROR_MESSAGES } from "../../constants/errorMessages";
import { addToBag, clearBag, fetchBagProducts } from "../../store/reducers/bag/apiThunk";
import { useShoppingCart } from "../../context/ShoppingCartContext";

export function useAddToCart(showCreateTrip, isDiscardModalOpen, productDetails, reset) {
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [error, setError] = useState("");
  const [outOfStock, setOutOfStock] = useState(false);
  const [discardTripLoader, setDiscardTripLoader] = useState(false);
  const [addtoCartLaoding, setLoading] = useState(false);
  const quantity = 1
  const dispatch = useDispatch();
  const { selectedProduct, setDiscardModalOpen, openModal, setBagItems, setBagItemsLoader, setTripDetails, tripDetails } = useShoppingCart();
  const { selectedTrip, setSelectedTrip, loadingTripDetails } = useCreateTrip(showCreateTrip, isDiscardModalOpen, setTripDetails)

  // Find matching variant based on selected options
  const findMatchingVariant = (options, variants) => {
    if (!variants?.length || !options || Object.keys(options || {})?.length === 0) {
      return null;
    }

    // Check if variants use new structure with option_values_with_hex
    const hasNewStructure = variants?.some(v => v?.option_values_with_hex) || false;

    if (hasNewStructure) {
      return variants?.find((variant) => {
        const variantOptions = variant?.option_values_with_hex || {};
        return Object.keys(options || {}).every((optionTypeName) => {
          const selectedValue = options?.[optionTypeName];
          const variantOptionValue = variantOptions?.[optionTypeName];
          // Handle both object format and direct value
          const variantValue = typeof variantOptionValue === 'object' && variantOptionValue !== null
            ? (variantOptionValue?.value || variantOptionValue)
            : variantOptionValue;
          return selectedValue === variantValue;
        });
      }) || null;
    }

    // Fallback to old structure (color/size)
    return null;
  };

  // Helper function to get color and size for backward compatibility
  const getColorAndSizeFromOptions = (options, variants) => {
    if (!variants?.length || !options || Object.keys(options || {})?.length === 0) {
      return { color: null, size: null };
    }

    // Check if variants use new structure
    const hasNewStructure = variants?.some(v => v?.option_values_with_hex) || false;
    if (hasNewStructure) {
      // For new structure, try to find matching variant and extract color/size if needed
      const matchingVariant = findMatchingVariant(options, variants);
      if (matchingVariant) {
        // Try to extract color (option with hex_code) and size (option named "Size")
        const firstVariant = variants?.[0];
        const optionValuesWithHex = firstVariant?.option_values_with_hex || {};

        let colorOptionType = null;
        let sizeOptionType = null;

        Object.keys(optionValuesWithHex || {}).forEach((optionTypeName) => {
          const optionValue = optionValuesWithHex?.[optionTypeName];
          const hexCode = typeof optionValue === 'object' && optionValue !== null
            ? optionValue?.hex_code
            : null;

          // Check if hex_code exists and is a string (including "000000" for black)
          if (hexCode !== null && hexCode !== undefined && typeof hexCode === 'string') {
            colorOptionType = optionTypeName;
          }
          if (optionTypeName?.toLowerCase()?.includes('size')) {
            sizeOptionType = optionTypeName;
          }
        });

        if (!sizeOptionType && colorOptionType) {
          const nonColorOptions = Object.keys(optionValuesWithHex || {}).filter(
            (name) => name !== colorOptionType
          );
          if (nonColorOptions?.length > 0) {
            sizeOptionType = nonColorOptions?.[0];
          }
        }

        return {
          color: colorOptionType ? options?.[colorOptionType] : null,
          size: sizeOptionType ? options?.[sizeOptionType] : null,
        };
      }
    }

    // For old structure, use existing logic
    return {
      color: selectedColor,
      size: selectedSize,
    };
  };

  // Update selectedColor and selectedSize when selectedOptions change (for backward compatibility)
  useEffect(() => {
    if (productDetails?.variants) {
      const hasNewStructure = productDetails?.variants?.some(v => v?.option_values_with_hex) || false;
      const hasVariantFlag = productDetails?.has_variant !== false;
      const shouldUseFirstVariant = !hasVariantFlag && productDetails?.variants?.length > 0;

      // Special case: has_variant is false but variants exist - automatically set first variant
      if (shouldUseFirstVariant) {
        const firstVariant = productDetails?.variants?.[0];
        if (firstVariant && firstVariant?.id !== selectedVariant?.id) {
          setSelectedVariant(firstVariant);
        }
        return;
      }

      if (Object.keys(selectedOptions || {})?.length > 0) {
        const { color, size } = getColorAndSizeFromOptions(selectedOptions, productDetails?.variants);
        if (color !== selectedColor) setSelectedColor(color);
        if (size !== selectedSize) setSelectedSize(size);

        // Also update selectedVariant for new structure
        if (hasNewStructure) {
          const matchingVariant = findMatchingVariant(selectedOptions, productDetails?.variants);
          if (matchingVariant && matchingVariant?.id !== selectedVariant?.id) {
            setSelectedVariant(matchingVariant);
          } else if (!matchingVariant && selectedVariant) {
            // Clear selectedVariant if no match found
            setSelectedVariant(null);
          }
        }
      } else {
        // Clear selectedVariant when options are cleared
        if (hasNewStructure && selectedVariant) {
          setSelectedVariant(null);
        }
      }
    }
    // eslint-disable-next-line
  }, [selectedOptions, productDetails?.variants, productDetails?.has_variant]);

  const onAddToCart = () => {
    let errorKey = "";

    // Check if we have new variant structure
    const hasNewStructure = productDetails?.variants?.some(v => v?.option_values_with_hex) || false;

    // For new structure, variants are optional - user can add without selecting all options
    // For old structure, we still need size/color for backward compatibility
    const hasVariants = productDetails?.variants?.length > 0;

    // Get current color and size from options (for backward compatibility)
    const { color, size } = getColorAndSizeFromOptions(selectedOptions, productDetails?.variants);

    // Validation logic - prioritize trip validation
    switch (true) {
      case !selectedTrip && !((tripDetails?.hotel_name?.length || 0) > 0 && (tripDetails?.city_name?.length || 0) > 0 && (tripDetails?.arrival_date?.length || 0) > 0):
        // Check if we need size/variant for old structure only
        if (!hasNewStructure && hasVariants && !size) {
          errorKey = "NO_TRIP_NO_SIZE";
        } else {
          errorKey = "NO_TRIP";
        }
        break;
      case !selectedTrip:
        // Check if we need size/variant for old structure only
        if (!hasNewStructure && hasVariants && !size) {
          errorKey = "NO_SELECTED_TRIP_NO_SIZE";
        } else {
          errorKey = "NO_SELECTED_TRIP";
        }
        break;
      case !tripDetails?.id:
        // Check if we need size/variant for old structure only
        if (!hasNewStructure && hasVariants && !size) {
          errorKey = "NO_TRIP_NO_SIZE";
        } else {
          errorKey = "NO_TRIP";
        }
        break;
      // Only validate size/color for old structure when variants exist
      case !hasNewStructure && hasVariants && !size:
        errorKey = "NO_SIZE";
        break;
      case !hasNewStructure && hasVariants && !color:
        errorKey = "NO_COLOR";
        break;
      default: {
        errorKey = "";
        addProductToBag()
      }
    }

    setError(errorKey ? ERROR_MESSAGES[errorKey] : "");
  };

  const addProductToBag = async () => {
    setLoading(true);

    // Check if variants use new structure
    const hasNewStructure = productDetails?.variants?.some(v => v?.option_values_with_hex);
    
    // Check if has_variant is false but variants exist (special case)
    const hasVariantFlag = productDetails?.has_variant !== false;
    const hasVariantsArray = productDetails?.variants?.length > 0;
    const shouldUseFirstVariant = !hasVariantFlag && hasVariantsArray;

    let data = {};

    // Special case: has_variant is false but variants exist - use first variant automatically
    if (shouldUseFirstVariant) {
      const firstVariant = productDetails?.variants?.[0];
      // Use shopify_variant_id if available, otherwise use id
      const variantId = firstVariant?.id;
      data = {
        "trip": tripDetails?.id,
        "product_id": productDetails?.id,
        "variant": variantId,
        "quantity": quantity
      };
    } else if (hasNewStructure) {
      // For new structure, use selectedVariant if available, otherwise send without variant
      if (selectedVariant?.id) {
        // Use variant for new structure when variant is selected
        data = {
          "trip": tripDetails?.id,
          "product_id": productDetails?.id,
          "variant": selectedVariant?.id,
          "quantity": quantity
        };
      } else {
        // Allow adding product without variant selection
        data = {
          "trip": tripDetails?.id,
          "product_id": productDetails?.id,
          "quantity": quantity
        };
      }
    } else {
      // Use old structure (color/size) - only if variants exist
      const { color, size } = getColorAndSizeFromOptions(selectedOptions, productDetails?.variants);
      if (productDetails?.variants?.length > 0) {
        data = {
          "trip": tripDetails?.id,
          "product_id": productDetails?.id,
          "size": size,
          "color": color,
          "quantity": quantity
        };
      } else {
        // No variants - add product directly
        data = {
          "trip": tripDetails?.id,
          "product_id": productDetails?.id,
          "quantity": quantity
        };
      }
    }
    const guestUserSessionKey = localStorage.getItem("guestUserSessionKey");
    let dataToSend;
    if (guestUserSessionKey) {
      dataToSend = {
        ...data,
        session_id: guestUserSessionKey
      }
    } else {
      dataToSend = data
    }

    try {
      await dispatch(addToBag(dataToSend)).then((resp) => {
        if (resp?.payload?.status)
          getCartDetails()
        else
          setLoading(false)
      })
    } catch (error) {
      console.error("Error setting favourite product:", error);
      setLoading(false)
    }
  }

  const getCartDetails = () => {
    setBagItemsLoader(true)
    dispatch(fetchBagProducts()).then((response) => {
      setBagItems(response?.payload?.data)
      setLoading(false)
    }).finally(() => {
      setBagItemsLoader(false)
    }
    )
  }


  const discardBag = async (tripId) => {
    setDiscardTripLoader(true)
    try {
      const response = await dispatch(clearBag(tripId))

      if (selectedProduct && response?.payload?.status) {
        reset()
        setDiscardModalOpen(false)
        setBagItems([])
      } else if (!selectedProduct && response?.payload?.status) {
        reset()
        openModal(null, null)
        setBagItems([])
      }
    } catch (error) {
      console.error("Error removing trip:", error);
    } finally {
      setDiscardTripLoader(false)
    }
  }

  useEffect(() => {
    setError("");
  }, [selectedSize, selectedTrip]);

  useEffect(() => {
    getCartDetails()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    selectedSize,
    setSelectedSize,
    selectedColor,
    setSelectedColor,
    selectedOptions,
    setSelectedOptions,
    selectedVariant,
    setError,
    error,
    onAddToCart,
    outOfStock,
    setOutOfStock,
    tripDetails,
    selectedTrip,
    setSelectedTrip,
    loadingTripDetails,
    discardBag,
    discardTripLoader,
    getCartDetails,
    addtoCartLaoding
  };
}
